import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useSwipeToClose from "../../../hooks/useSwipeToClose";

const ImagePreviewDrawer = ({ isOpen, image, closeDrawer }) => {
  const {
    containerRef,
    dragOffset,
    isAnimating,
    shouldRender,
    isVisible,
    isDragging,
    DragHandle,
  } = useSwipeToClose(closeDrawer, isOpen);
  const portalContainerRef = useRef(null);

  useEffect(() => {
    const container = document.createElement("div");
    container.id = "image-preview-portal";
    container.style.position = "relative";
    container.style.zIndex = "99999";
    document.body.appendChild(container);
    portalContainerRef.current = container;

    return () => {
      if (container && document.body.contains(container)) {
        document.body.removeChild(container);
      }
    };
  }, []);

  if (!shouldRender || !portalContainerRef.current) return null;

  return createPortal(
    <div
      className="fixed flex items-end justify-center inset-0 bg-[#0D0C07] drawer-overlay"
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: "background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        zIndex: 99999,
      }}
      onClick={closeDrawer}
    >
      <div
        ref={containerRef}
        className="flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content max-h-[80dvh]"
        style={{
          transform: isVisible
            ? `translateY(${Math.max(0, dragOffset)}px)`
            : "translateY(100%)",
          transition:
            (dragOffset === 0 && isAnimating) ||
            (!isDragging && dragOffset === 0)
              ? "transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)"
              : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragHandle />
        <button
          onClick={closeDrawer}
          className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-20"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z"
              fill="#56554D"
            />
          </svg>
        </button>

        <div className="flex-1 overflow-y-auto min-h-0 overflow-x-hidden p-6 pt-8 flex flex-col items-center justify-center">
          {image?.name && (
            <h2 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[16px] font-bold leading-[32px] w-full text-center mb-4">
              {image.name}
            </h2>
          )}
          <div className="w-full flex items-center justify-center">
            <img
              src={image?.src}
              alt={image?.name || "Product preview"}
              className="max-w-full max-h-[60vh] object-contain rounded-[12px]"
              onError={(e) => {
                e.target.src = "/assets/products/no-image.jpg";
              }}
            />
          </div>
        </div>
      </div>
    </div>,
    portalContainerRef.current
  );
};

export default ImagePreviewDrawer;
